import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { effectiveWorkspaceLimit, resolveLibraryMode, type Tier } from "@/lib/tier";
import { useAuth } from "./useAuth";
import { useSubscription } from "./useSubscription";
import { useWorkspaceAddons } from "./useWorkspaceAddons";
import { useTierOverride } from "./useTierOverride";
import { useUserRole } from "./useUserRole";
import { WORKSPACE_STORAGE_KEY, WorkspaceContext, type Workspace } from "./useWorkspace";

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const queryKey = ["workspaces", userId] as const;

  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(WORKSPACE_STORAGE_KEY));

  // Cached via TanStack Query so the profiles fetch is deduped across
  // remounts and consumers — no full-page Loading flash on navigation.
  const { data: workspaces = [], isLoading, isFetched } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    queryFn: async (): Promise<Workspace[]> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, owner_user_id, display_name, workspace_label, default_library_category_id, is_primary, account_tier, sort_order, library_isolated, brand_handle, brand_handle_changed_at, workspace_font_preference, codex_enabled, parent_profile_id")
        .eq("owner_user_id", userId!)
        .order("is_primary", { ascending: false })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Workspace[];
    },
  });

  // Only "loading" on the very first resolution — cached subsequent renders
  // (e.g. after remount) return synchronously with isFetched=true.
  const loading = !!userId && isLoading && !isFetched;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey });
  };

  useEffect(() => {
    if (!workspaces.length) return;
    if (!activeId || !workspaces.find(w => w.id === activeId)) {
      const primary = workspaces.find(w => w.is_primary) ?? workspaces[0];
      setActiveId(primary.id);
      localStorage.setItem(WORKSPACE_STORAGE_KEY, primary.id);
    }
  }, [workspaces, activeId]);

  const setActive = (id: string) => {
    setActiveId(id);
    localStorage.setItem(WORKSPACE_STORAGE_KEY, id);
  };

  const { subscription } = useSubscription();
  const { count: addonQty } = useWorkspaceAddons();
  const { isSuperAdmin } = useUserRole();
  void subscription;

  // Persona-aware tier resolution: when active workspace is itself a Persona
  // (is_primary=true), its tier wins. Non-primary workspaces inherit from
  // their parent Persona (or fall back to the first primary for legacy rows).
  const activeRaw = workspaces.find(w => w.id === activeId) ?? null;
  const firstPrimary = workspaces.find(w => w.is_primary) ?? null;
  const activePersona = activeRaw?.is_primary
    ? activeRaw
    : (activeRaw?.parent_profile_id
        ? (workspaces.find(w => w.id === activeRaw.parent_profile_id) ?? firstPrimary)
        : firstPrimary);
  const activePersonaId = activePersona?.id ?? null;
  const realPrimaryTier: Tier | null = activePersona?.account_tier ?? activeRaw?.account_tier ?? null;

  // Super-admin "View as tier" override (frontend-only; never affects DB or edge functions).
  const { override: tierOverride } = useTierOverride();
  const primaryTier: Tier | null = tierOverride ?? realPrimaryTier;

  // When override is set, remap the active Persona's tier so downstream
  // consumers reading `active.account_tier` reflect the preview.
  const effectiveWorkspaces = useMemo(() => {
    if (!tierOverride || !activePersona) return workspaces;
    return workspaces.map(w => (w.id === activePersona.id ? { ...w, account_tier: tierOverride } : w));
  }, [workspaces, tierOverride, activePersona]);

  // Scope to the active Persona: the Persona itself + its child workspaces.
  // Used for tabs, counts, and create gates so other Personas don't bleed in.
  const scopedWorkspaces = useMemo(() => {
    if (!activePersonaId) return effectiveWorkspaces;
    return effectiveWorkspaces.filter(
      w => w.id === activePersonaId || w.parent_profile_id === activePersonaId,
    );
  }, [effectiveWorkspaces, activePersonaId]);

  const active = effectiveWorkspaces.find(w => w.id === activeId) ?? null;

  // Super-admin acting on a workspace they own: bypass caps & creation gates. Default ON.
  const superAdminBypassCaps = !!(isSuperAdmin && active && user && active.owner_user_id === user.id);

  const workspaceLimit = effectiveWorkspaceLimit(primaryTier, addonQty);
  const canCreateWorkspace = superAdminBypassCaps
    ? true
    : (workspaceLimit === null ? true : scopedWorkspaces.length < workspaceLimit);

  const libraryMode = useMemo(
    () => resolveLibraryMode(primaryTier, scopedWorkspaces.length, active?.library_isolated ?? false),
    [primaryTier, scopedWorkspaces.length, active?.library_isolated],
  );

  return (
    <WorkspaceContext.Provider value={{ workspaces: scopedWorkspaces, allOwnedWorkspaces: effectiveWorkspaces, active, activePersonaId, setActive, refresh, loading, workspaceLimit, canCreateWorkspace, superAdminBypassCaps, primaryTier, realPrimaryTier, libraryMode }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
